import { useState, useEffect, useRef } from "react";
import { Link, useRouter, usePathname } from "@/lib/navigation";
import AppImage from "@/components/AppImage";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./Header.module.css";
import { useSearch } from "../context/SearchContext";
import { useAuth } from "../context/AuthContext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import IsbnScanner from "./IsbnScanner";
import { useNotifications } from "../context/NotificationContext";
import NotificationsModal from "./NotificationsModal";

export default function Header({ initialSearchQuery: _initialSearchQuery = "" }) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] =
    useState(false);
  const searchContainerRef = useRef(null);

  const { searchQuery, setSearchQuery, loading } = useSearch();
  const router = useRouter();
  const pathname = usePathname();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const previousPathRef = useRef(pathname);
  const { user, logout } = useAuth();
  const [isProfileDropdownVisible, setIsProfileDropdownVisible] =
    useState(false);
  const profileDropdownRef = useRef(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { unreadCount } = useNotifications();

  // Clear search when navigating away from search page (but preserve for book pages)
  useEffect(() => {
    const isNavigatingToBook = pathname.startsWith("/books/");
    const isComingFromSearch = previousPathRef.current === "/search";

    // Only clear search if navigating away from search and NOT to a book page
    if (isComingFromSearch && pathname !== "/search" && !isNavigatingToBook) {
      setSearchQuery("");
    }
    previousPathRef.current = pathname;
  }, [pathname, setSearchQuery]);

  // Load recent searches from localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem("recentSearches");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Modify effect to handle search page behavior
  useEffect(() => {
    if (pathname === "/search") {
      setIsMobileSearchOpen(true);
      if (window.innerWidth > 768) {
        setIsSearchFocused(true);
        setIsDropdownVisible(true);
      }
    }
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileDropdownVisible(false);
      router.push("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  // Handle clicks outside search container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!searchContainerRef.current?.contains(event.target)) {
        handleDropdownClose();
      }
      if (!profileDropdownRef.current?.contains(event.target)) {
        setIsProfileDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addToRecentSearches = (term) => {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) return;

    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s !== trimmedTerm);
      const updated = [trimmedTerm, ...filtered].slice(0, 5);
      localStorage.setItem("recentSearches", JSON.stringify(updated));
      return updated;
    });
  };

  // Handle search focus
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setIsDropdownVisible(true);
  };

  // Handle mobile search click
  const handleMobileSearchClick = (e) => {
    e.stopPropagation();
    if (window.innerWidth <= 768) {
      setIsMobileSearchOpen(true);
      setIsSearchFocused(true);
      setIsDropdownVisible(true);
    }
  };

  // Modify handleMobileSearchClose to prevent closing on search page
  const handleMobileSearchClose = (e) => {
    e.stopPropagation();
    if (pathname !== "/search") {
      setIsMobileSearchOpen(false);
      handleDropdownClose();
    } else {
      // On search page, just close the dropdown but keep search open
      setIsDropdownVisible(false);
      setIsSearchFocused(false);
    }
  };

  // Modify handleDropdownClose to respect search page on mobile
  const handleDropdownClose = () => {
    setIsDropdownVisible(false);
    setSelectedIndex(-1);
    setTimeout(() => {
      setIsSearchFocused(false);
      if (window.innerWidth <= 768 && pathname !== "/search") {
        setIsMobileSearchOpen(false);
      }
    }, 200);
  };

  const handleSearchInput = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
  };

  const handleSearchSubmit = (term = searchQuery) => {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) return;

    addToRecentSearches(trimmedTerm);
    setSearchQuery(trimmedTerm);
    router.push("/search"); // Just navigate to search page
    handleDropdownClose();
  };

  const handleKeyDown = (e) => {
    if (!isSearchFocused || !recentSearches.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < recentSearches.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex > -1) {
          handleSearchSubmit(recentSearches[selectedIndex]);
        } else if (searchQuery.trim()) {
          handleSearchSubmit();
        }
        break;
      case "Escape":
        e.preventDefault();
        handleDropdownClose();
        break;
    }
  };

  const clearSearch = (e) => {
    if (e) e.stopPropagation();

    setSearchQuery(""); // Always clear the query

    // Only close search on desktop when not on search page
    if (window.innerWidth > 768 && pathname !== "/search") {
      handleDropdownClose();
      setIsMobileSearchOpen(false);
    }
  };

  const handleScan = (isbn) => {
    setSearchQuery(isbn);
    handleSearchSubmit(isbn);
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.logo}>
          <Link to="/">
            <AppImage
              src="/logo.png"
              alt="NovelNet Logo"
              width={140}
              height={80}
              priority
              className={styles.logoImage}
            />
          </Link>
        </div>

        <div className={styles.searchWrapper}>
          <div
            ref={searchContainerRef}
            className={`${styles.searchContainer} ${
              isSearchFocused ? styles.focused : ""
            } ${isMobileSearchOpen ? styles.mobileOpen : ""}`}
            onClick={handleMobileSearchClick}
          >
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder={
                loading ? "Searching..." : "Search books, users or lists..."
              }
              value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={handleKeyDown}
              className={styles.searchInput}
              onFocus={handleSearchFocus}
            />
            {isMobileSearchOpen && (
              <button
                className={styles.cameraButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsScannerOpen(true);
                }}
                aria-label="Scan ISBN"
              >
                <PhotoCameraIcon />
              </button>
            )}
            {(searchQuery || isMobileSearchOpen) && (
              <button
                className={styles.clearButton}
                onClick={(e) => clearSearch(e)}
                aria-label="Clear search"
              >
                <CloseIcon />
              </button>
            )}
          </div>
          <div
            className={styles.searchOverlay}
            onClick={handleMobileSearchClose}
          ></div>
          {isSearchFocused && recentSearches.length > 0 && (
            <div
              className={`${styles.searchDropdown} ${
                isDropdownVisible ? styles.fadeIn : styles.fadeOut
              }`}
            >
              <div className={styles.dropdownHeader}>
                <span>Recent Searches</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    localStorage.removeItem("recentSearches");
                    setRecentSearches([]);
                    handleDropdownClose();
                  }}
                  className={styles.clearAllButton}
                >
                  Clear All
                </button>
              </div>
              {recentSearches.map((term, index) => (
                <button
                  key={term}
                  className={`${styles.recentSearchItem} ${
                    index === selectedIndex ? styles.selected : ""
                  }`}
                  onClick={() => handleSearchSubmit(term)}
                >
                  <SearchIcon className={styles.smallSearchIcon} />
                  {term}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={styles.authContainer} ref={profileDropdownRef}>
          {user ? (
            <div className={styles.profileContainer}>
              <button
                className={styles.accountButton}
                onClick={() =>
                  setIsProfileDropdownVisible(!isProfileDropdownVisible)
                }
              >
                <AccountCircleIcon />
                <span className={styles.accountText}>
                  {user.username}
                  {unreadCount > 0 && (
                    <span className={styles.notificationBadge}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </span>
              </button>
              {isProfileDropdownVisible && (
                <div className={styles.profileDropdownContainer}>
                  <div className={`${styles.profileDropdown} ${styles.fadeIn}`}>
                    <Link
                      href={`/users/${user.username}`}
                      className={styles.profileDropdownItem}
                      onClick={() => setIsProfileDropdownVisible(false)}
                    >
                      Profile
                    </Link>
                    <button
                      className={styles.profileDropdownItem}
                      onClick={() => {
                        setIsProfileDropdownVisible(false);
                        setIsNotificationsModalOpen(true);
                      }}
                    >
                      Notifications
                      {unreadCount > 0 && (
                        <span className={styles.dropdownBadge}>
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </button>
                    <Link
                      href="/settings"
                      className={styles.profileDropdownItem}
                      onClick={() => setIsProfileDropdownVisible(false)}
                    >
                      Settings
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsProfileDropdownVisible(false);
                      }}
                      className={`${styles.profileDropdownItem} ${styles.logoutItem}`}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className={styles.loginButton}>
              <AccountCircleIcon />
              <span className={styles.loginText}>Login / Register</span>
            </Link>
          )}
        </div>
      </header>
      {isScannerOpen && (
        <IsbnScanner
          onScan={handleScan}
          onClose={() => setIsScannerOpen(false)}
        />
      )}
      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={() => setIsNotificationsModalOpen(false)}
      />
    </>
  );
}
